import {
  argbFromHex,
  hexFromArgb,
  themeFromSourceColor,
} from "@material/material-color-utilities";

export type ArtistMaterialTheme = {
  source: string; // normalized source hex (#RRGGBB)
  primary: string;
  primaryInk: string; // darker/safer variant for text on light backgrounds
  onPrimary: string;
  secondary: string;
  onSecondary: string;
  background: string;
  onBackground: string;
  outline: string;
  tint: string; // subtle glow/tint derived from primary
};

/**
 * Normalizes a hex color to #RRGGBB format
 */
function normalizeHex(hex: string): string {
  // Remove # if present
  const cleaned = hex.replace(/^#/, "");

  // Handle 3-character hex
  if (cleaned.length === 3) {
    return `#${cleaned
      .split("")
      .map((char) => char + char)
      .join("")}`;
  }

  // Ensure 6-character hex
  if (cleaned.length === 6) {
    return `#${cleaned}`;
  }

  throw new Error(`Invalid hex color: ${hex}`);
}

/**
 * Converts hex string to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  // Explicitly match both uppercase and lowercase hex characters
  const result = /^#?([a-fA-F\d]{2})([a-fA-F\d]{2})([a-fA-F\d]{2})$/.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/**
 * Converts RGB values to hex string
 */
function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((x) => {
      const hex = x.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    })
    .join("")}`;
}

/**
 * Calculates relative luminance (for WCAG contrast)
 */
function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculates contrast ratio between two colors (WCAG)
 */
function contrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  const lum1 = relativeLuminance(rgb1);
  const lum2 = relativeLuminance(rgb2);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Mixes two hex colors
 */
function mix(hex1: string, hex2: string, amount: number): string {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);

  const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * amount);
  const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * amount);
  const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * amount);

  return rgbToHex(r, g, b);
}

/**
 * Generates an accessible Material theme from a source hex color
 */
export function getArtistMaterialTheme(sourceHex: string): ArtistMaterialTheme {
  // Normalize the source hex
  const normalizedHex = normalizeHex(sourceHex);

  // Convert hex to ARGB (Material Color Utilities format)
  const sourceArgb = argbFromHex(normalizedHex);

  // Generate Material theme from source color
  const theme = themeFromSourceColor(sourceArgb);

  // Extract colors from the light scheme
  const lightScheme = theme.schemes.light;

  // Get primary colors from scheme
  const primary = hexFromArgb(lightScheme.primary);
  let onPrimary = hexFromArgb(lightScheme.onPrimary);
  const secondary = hexFromArgb(lightScheme.secondary);
  const onSecondary = hexFromArgb(lightScheme.onSecondary);
  const background = hexFromArgb(lightScheme.background);
  const onBackground = hexFromArgb(lightScheme.onBackground);
  const outline = hexFromArgb(lightScheme.outline);

  // Guard 1: Ensure onPrimary is readable on primary
  const primaryContrast = contrastRatio(primary, onPrimary);
  if (primaryContrast < 4.5) {
    // Determine if primary is bright or dark
    const primaryLum = relativeLuminance(hexToRgb(primary));
    onPrimary = primaryLum > 0.5 ? "#111827" : "#ffffff";
  }

  // Guard 2: Compute primaryInk for colored text on light backgrounds
  let primaryInk = primary;
  const primaryBackgroundContrast = contrastRatio(primary, background);

  if (primaryBackgroundContrast < 4.5) {
    // Darken primary by mixing with black until contrast >= 4.5
    let currentInk: string = primary; // Initialize with primary as fallback
    for (let i = 0; i < 30; i++) {
      const amount = (i + 1) / 30; // Gradually increase black mix
      currentInk = mix(primary, "#000000", amount);
      const contrast = contrastRatio(currentInk, background);
      if (contrast >= 4.5) {
        primaryInk = currentInk;
        break;
      }
      // If we've reached max iterations, use the darkest we got
      if (i === 29) {
        primaryInk = currentInk;
      }
    }
  }

  // Compute tint (very light version of primary for gradients)
  const tint = mix(primary, "#ffffff", 0.88);

  return {
    source: normalizedHex,
    primary,
    primaryInk,
    onPrimary,
    secondary,
    onSecondary,
    background,
    onBackground,
    outline,
    tint,
  };
}
